import { Badge, Button, DataTable, Tooltip } from '@commenda/kakashi';

import {
  IIndirectTaxFilingTask,
  IndirectTaxFilingClientActionType,
} from '../types';

interface IndirectTaxFilingDetailTaskTableProps {
  tasks: IIndirectTaxFilingTask[];
  onTaskAction: (task: IIndirectTaxFilingTask) => void;
  // Contribution-driven filings let the client send the filing back to the
  // agent via a "Report issue" action alongside "Approve". Omitted for US.
  isContributionDriven?: boolean;
  onReportIssue?: (task: IIndirectTaxFilingTask) => void;
  // Agents can monitor the pending step but not act on it: the action button
  // renders disabled with an explanatory tooltip.
  isActionDisabled?: boolean;
}

const DISABLED_ACTION_TOOLTIP = 'Only the customer can approve this filing.';

const ACTION_LABELS: Record<IndirectTaxFilingClientActionType, string> = {
  [IndirectTaxFilingClientActionType.APPROVE]: 'Approve',
};

const IndirectTaxFilingDetailTaskTable = ({
  tasks,
  onTaskAction,
  isContributionDriven = false,
  onReportIssue,
  isActionDisabled = false,
}: IndirectTaxFilingDetailTaskTableProps) => {
  return (
    <DataTable
      data={tasks}
      columnGroups={[
        {
          id: 'filing-detail-task-info',
          columns: [
            {
              accessorKey: 'title',
              header: 'Task',
              title: 'Task',
              showInSelector: false,
              enableSorting: false,
              cell: ({ row }) => (
                <div className="flex flex-col">
                  <span className="txt-small-medium text-text-primary">
                    {row.original.title}
                  </span>
                  <span className="txt-extra-small-regular text-text-secondary">
                    {row.original.description}
                  </span>
                </div>
              ),
            },
            {
              accessorKey: 'status',
              header: 'Status',
              title: 'Status',
              showInSelector: false,
              enableSorting: false,
              cell: () => (
                <Badge variant="warning" size="sm">
                  Pending
                </Badge>
              ),
            },
            {
              accessorKey: 'action',
              header: '',
              title: 'Action',
              showInSelector: false,
              enableSorting: false,
              cell: ({ row }) => (
                <div className="flex items-center justify-end gap-md">
                  {isContributionDriven &&
                    onReportIssue &&
                    row.original.actionType ===
                      IndirectTaxFilingClientActionType.APPROVE && (
                      <Button
                        variant="destructive-tertiary"
                        size="sm"
                        onClick={() => onReportIssue(row.original)}
                      >
                        Report issue
                      </Button>
                    )}
                  <Tooltip
                    content={DISABLED_ACTION_TOOLTIP}
                    disabled={!isActionDisabled}
                  >
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={isActionDisabled}
                      onClick={() => onTaskAction(row.original)}
                    >
                      {ACTION_LABELS[row.original.actionType]}
                    </Button>
                  </Tooltip>
                </div>
              ),
            },
          ],
        },
      ]}
      searchConfig={{ isEnabled: false, columnsToEnableSearchFor: [] }}
      tableViewConfig={{ isEnabled: false, defaultView: 'regular' }}
    />
  );
};

export { IndirectTaxFilingDetailTaskTable };
